import { nanoid } from "nanoid";
import type {
  AddressFieldDefinition,
  CalculatedFieldDefinition,
  ChoiceFieldDefinition,
  DateFieldDefinition,
  DropdownFieldDefinition,
  EmailFieldDefinition,
  FileUploadFieldDefinition,
  FormFieldDefinition,
  FormFieldKind,
  FormSectionDefinition,
  LongTextFieldDefinition,
  NameFieldDefinition,
  NumberFieldDefinition,
  PhoneFieldDefinition,
  RatingFieldDefinition,
  ShortTextFieldDefinition,
  StaticTextFieldDefinition,
  TimeFieldDefinition,
} from "@rwaforms/shared";

export function createSection(title: string, position: number): FormSectionDefinition {
  return {
    id: `section_${nanoid(8)}`,
    title,
    description: "",
    position,
    layout: "single_column",
    parentSectionId: null,
    settings: {},
  };
}

export function createField(
  kind: FormFieldKind,
  position: number,
  sectionId?: string | null,
  overrides?: Partial<FormFieldDefinition>
): FormFieldDefinition {
  const base = {
    id: `${kind}_${nanoid(10)}`,
    type: kind,
    title: prettifyTitle(kind),
    description: "",
    required: false,
    sectionId: sectionId ?? null,
    position,
    tags: [],
    metadata: {},
    visibility: "visible" as const,
  };

  switch (kind) {
    case "short_text":
      return {
        ...base,
        type: "short_text" as const,
        placeholder: "Enter text",
        validation: {},
        ...overrides,
      } as ShortTextFieldDefinition;
    case "long_text":
      return {
        ...base,
        type: "long_text" as const,
        placeholder: "Provide details",
        validation: {},
        ...overrides,
      } as LongTextFieldDefinition;
    case "email":
      return {
        ...base,
        type: "email" as const,
        placeholder: "your@email.com",
        validation: {},
        ...overrides,
      } as EmailFieldDefinition;
    case "phone":
      return {
        ...base,
        type: "phone" as const,
        placeholder: "+91 98765 43210",
        validation: { format: "international" as const },
        ...overrides,
      } as PhoneFieldDefinition;
    case "name":
      return {
        ...base,
        type: "name" as const,
        placeholder: "Full name",
        showMiddleName: false,
        showPrefix: false,
        showSuffix: false,
        ...overrides,
      } as NameFieldDefinition;
    case "number":
      return {
        ...base,
        type: "number" as const,
        prefix: "",
        suffix: "",
        validation: { allowDecimals: true },
        ...overrides,
      } as NumberFieldDefinition;
    case "rating":
      return {
        ...base,
        type: "rating" as const,
        style: "stars",
        scale: 5,
        lowLabel: "Not satisfied",
        highLabel: "Very satisfied",
        ...overrides,
      } as RatingFieldDefinition;
    case "multiple_choice":
      return {
        ...base,
        type: "multiple_choice" as const,
        options: defaultOptions(),
        allowOther: false,
        randomizeOptions: false,
        ...overrides,
      } as ChoiceFieldDefinition;
    case "checkboxes":
      return {
        ...base,
        type: "checkboxes" as const,
        options: defaultOptions(),
        maxSelections: undefined,
        allowOther: true,
        ...overrides,
      } as ChoiceFieldDefinition;
    case "dropdown":
      return {
        ...base,
        type: "dropdown" as const,
        options: defaultOptions(),
        allowMultiple: false,
        allowSearch: true,
        ...overrides,
      } as DropdownFieldDefinition;
    case "file_upload":
      return {
        ...base,
        type: "file_upload" as const,
        title: "File Upload",
        maxFileSizeMB: 25,
        maxFiles: 1,
        allowedTypes: ["image/*", ".pdf"],
        ...overrides,
      } as FileUploadFieldDefinition;
    case "date":
      return {
        ...base,
        type: "date" as const,
        validation: {},
        ...overrides,
      } as DateFieldDefinition;
    case "time":
      return {
        ...base,
        type: "time" as const,
        validation: { timezoneMode: "submitter" },
        ...overrides,
      } as TimeFieldDefinition;
    case "calculated":
      return {
        ...base,
        type: "calculated" as const,
        expression: "",
        inputFieldIds: [],
        outputType: "number",
        precision: 2,
        visibility: "read_only",
        ...overrides,
      } as CalculatedFieldDefinition;
    case "static_text":
      return {
        ...base,
        type: "static_text" as const,
        title: "",
        content: "Heading",
        format: "markdown",
        visibility: "visible",
        ...overrides,
      } as StaticTextFieldDefinition;
    case "address":
      return {
        ...base,
        type: "address" as const,
        title: "Address",
        defaultCountry: "India",
        labels: {
          houseNumber: "House Number",
          village: "Village",
          post: "Post",
          district: "District",
          state: "State",
          country: "Country",
          pincode: "Pincode",
        },
        placeholders: {
          houseNumber: "Enter house/flat number",
          village: "Enter village name",
          post: "Enter post office name",
          district: "Enter district name",
          pincode: "Enter 6-digit pincode",
        },
        requiredFields: {
          houseNumber: true,
          village: true,
          post: true,
          district: true,
          state: true,
          pincode: true,
        },
        ...overrides,
      } as AddressFieldDefinition;
    default:
      return base as FormFieldDefinition;
  }
}

function defaultOptions() {
  return [
    { id: `opt_${nanoid(6)}`, label: "Option 1" },
    { id: `opt_${nanoid(6)}`, label: "Option 2" },
    { id: `opt_${nanoid(6)}`, label: "Option 3" },
  ];
}

function prettifyTitle(kind: FormFieldKind): string {
  switch (kind) {
    case "short_text":
      return "Short Answer";
    case "long_text":
      return "Paragraph";
    case "email":
      return "Email";
    case "phone":
      return "Phone Number";
    case "name":
      return "Name";
    case "number":
      return "Number";
    case "multiple_choice":
      return "Single Select";
    case "checkboxes":
      return "Multi Select";
    case "dropdown":
      return "Dropdown";
    case "rating":
      return "Rating";
    case "file_upload":
      return "Upload";
    case "date":
      return "Date";
    case "time":
      return "Time";
    case "calculated":
      return "Calculated";
    case "static_text":
      return "Content Block";
    case "address":
      return "Address";
    default:
      return "Field";
  }
}
